import { ErrorResponse } from 'react-router-dom';
import {
  IDomesticInsuranceDataSource,
  IDomesticInsuranceInitialValues,
  IResponse,
} from '../types';
import { IUseHttpServiceProps } from '../services/type';
import { RequestMethods } from '../services/enum';
import { IExpandedRow } from '../components/DataTable/type';
import { prepareFormData } from '../utils/functions';

export default class DomesticInsuranceService {
  private readonly baseUrl = 'main-insurances/domestic-insurance/';

  constructor(
    private readonly executeService: <T>(
      args: IUseHttpServiceProps,
    ) => Promise<T | ErrorResponse | null>,
    private readonly parameters: string,
  ) {}

  async fetchData(): Promise<
    IResponse<IDomesticInsuranceDataSource> | ErrorResponse | null
  > {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${this.parameters}`,
    });
  }

  async fetchItemById(
    id: number,
  ): Promise<IDomesticInsuranceDataSource | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async fetchExpandedRows(
    id: number,
  ): Promise<IExpandedRow[] | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/get-latest-processes/`,
    });
  }

  async deleteItemById(id: number): Promise<unknown | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.DELETE,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async cancelPolicy(id: number): Promise<unknown | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('id', id.toString());
    formData.append('status', 'cancel_expected');

    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/update-status/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async exportData(): Promise<{ download_url: string } | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}export-excel/${this.parameters}`,
      contentType: 'multipart/form-data',
    });
  }

  async submitForm(
    values: IDomesticInsuranceInitialValues,
    selectedItem?: IDomesticInsuranceDataSource,
  ): Promise<IDomesticInsuranceDataSource | ErrorResponse | null> {
    const formData = prepareFormData<IDomesticInsuranceInitialValues>(values);

    const method = selectedItem ? RequestMethods.PUT : RequestMethods.POST;
    const url = selectedItem
      ? `${this.baseUrl}${selectedItem.id}/`
      : this.baseUrl;

    return await this.executeService<IDomesticInsuranceDataSource>({
      method,
      url,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async updateStatus(
    id: number,
    status: string,
    email_users: number[],
  ): Promise<IDomesticInsuranceDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('email_users', email_users.join(','));

    return await this.executeService<IDomesticInsuranceDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/update-status/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async uploadFiles(
    id: number,
    files: FileList,
  ): Promise<IDomesticInsuranceDataSource | ErrorResponse | null> {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('upload_files', file));

    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/upload-files/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async deleteFile(
    id: number,
    fileId: number,
  ): Promise<IDomesticInsuranceDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('file_id', fileId.toString());

    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/delete-file/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async submitAddendum(
    id: number,
    description: string,
    file: File | undefined,
  ): Promise<unknown | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('id', id.toString());
    if (file) formData.append('file', file);
    formData.append('description', description);

    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/endorsement-request/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }
}
